/**
 * app.config.js — Centralized client configuration file.
 *
 * PURPOSE:
 * Allows clients/deployers to customize the app's appearance and behavior
 * without modifying source code. This file is loaded once on app startup
 * and its values are applied globally.
 *
 * USAGE:
 * Import this config in main.js or any component:
 *   import appConfig from '@/app.config';
 *
 * Override at deploy time by editing this file or by injecting values
 * via environment variables (VUE_APP_* prefix for Vue CLI).
 */

const appConfig = {
  // ─────────────────────────────────────────────────────────────────────
  // THEME — Default visual appearance
  // These values are applied as CSS custom properties on startup.
  // Per-tenant overrides from the API (ConfiguracionHelper) take priority.
  // ─────────────────────────────────────────────────────────────────────
  theme: {
    /**
     * Overrides de marca (white-label). Deben quedar en null salvo que el
     * deploy los defina: si se setean, se aplican como inline style sobre
     * <html> y por especificidad ganan a los valores por tema de index.css,
     * dejando el mismo indigo en claro y en oscuro. El primario de cada tema
     * está calibrado por separado (indigo-600 en claro, indigo-500 en oscuro),
     * así que solo hay que pisarlo cuando el cliente trae su propio color.
     */
    primary_color: process.env.VUE_APP_THEME_PRIMARY || null,
    secondary_color: process.env.VUE_APP_THEME_SECONDARY || null,
    /** Fallback de marca para usos fuera del CSS (ej: color del loader) */
    fallback_primary_color: '#6366f1',
    accent_color: process.env.VUE_APP_THEME_ACCENT || '#8b5cf6',
    background_color: '#111827',
    background_secondary_color: '#1f2937',
    text_color: '#ffffff',
    text_secondary_color: '#9ca3af',
    border_color: '#374151',
    success_color: '#22c55e',
    error_color: '#ef4444',
    warning_color: '#eab308',
    /** 'light' or 'dark' — default mode applied on startup */
    default_mode: 'dark',
  },

  // ─────────────────────────────────────────────────────────────────────
  // BRANDING — Application identity
  // ─────────────────────────────────────────────────────────────────────
  branding: {
    app_name: process.env.VUE_APP_NAME || 'Olitom',
    /** Shown in PWA install prompts and browser title */
    app_description:
      'Sistema de gestion para emprendimientos en Argentina con presupuestos, ventas, cuenta corriente, IA y estadisticas inteligentes',
    /** Path relative to /public or full URL */
    logo_url: '/img/icons/favicon.png',
    favicon_url: '/img/icons/favicon.png',
  },

  // ─────────────────────────────────────────────────────────────────────
  // FEATURE FLAGS — Toggle capabilities without code changes
  // Add new flags here as the product evolves.
  // ─────────────────────────────────────────────────────────────────────
  features: {
    /** Enable the advertising slot component */
    ads_enabled: true,
    /** Enable AI-powered features (Gemini integration) */
    ai_enabled: true,
    /** Enable public order tracking pages */
    tracking_enabled: true,

    /** Enable the feedback/support ticket button */
    feedback_enabled: true,
    /** Enable expense management module */
    gastos_enabled: true,
    /** Enable quotes/presupuestos module */
    presupuestos_enabled: true,
    /** Enable cuenta corriente (client accounts) module */
    cuenta_corriente_enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────
  // WHATSAPP — Canal de alta de cuentas
  //
  // El auto-registro está cerrado: las cuentas se activan a mano después de
  // un mensaje. Cada CTA de la landing abre WhatsApp con el texto ya escrito.
  // ─────────────────────────────────────────────────────────────────────
  whatsapp: {
    /** Número en formato internacional, solo dígitos. Ej: 5493513456789 */
    number: process.env.VUE_APP_WHATSAPP_NUMBER || '',
    /** Días de prueba que se ofrecen. Se interpola en toda la copy. */
    trial_days: Number(process.env.VUE_APP_TRIAL_DAYS) || 30,
  },

  // ─────────────────────────────────────────────────────────────────────
  // LOCALE — Regional defaults
  // ─────────────────────────────────────────────────────────────────────
  locale: {
    currency_code: 'ARS',
    currency_symbol: '$',
    date_format: 'DD/MM/YYYY',
    thousands_separator: '.',
    decimal_separator: ',',
    decimal_places: 2,
    language: 'es-AR',
  },

  // ─────────────────────────────────────────────────────────────────────
  // API — Backend connection (overridden by env vars in practice)
  // ─────────────────────────────────────────────────────────────────────
  api: {
    base_url: process.env.VUE_APP_API_URL || 'http://localhost:3000',
    timeout: 30000,
  },
};

export default appConfig;
